import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { validateStudentApplicationsBulk, commitStudentApplicationsBulk } from "@/lib/api";

const SAMPLE_HEADERS = [
  "application_number",
  "name",
  "phone_number",
  "mail_id",
  "roll_no",
  "branch",
  "gender",
  "student_category_id",
  "father_mail",
  "mother_mail",
  "skip_policies",
];

/** Minimal CSV parser — handles quoted fields and commas. */
function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  const pushCell = () => { row.push(cell); cell = ""; };
  const pushRow = () => {
    if (row.length > 1 || (row.length === 1 && String(row[0]).trim() !== "")) {
      rows.push(row);
    }
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') { cell += '"'; i += 1; }
      else if (ch === '"') inQuotes = false;
      else cell += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ",") { pushCell(); continue; }
    if (ch === "\n") { pushCell(); pushRow(); continue; }
    if (ch === "\r") continue;
    cell += ch;
  }
  pushCell();
  pushRow();

  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => String(h || "").trim());
  return rows.slice(1).map((cols) => {
    const obj = {};
    headers.forEach((h, idx) => {
      if (!h) return;
      obj[h] = cols[idx] != null ? String(cols[idx]).trim() : "";
    });
    return obj;
  });
}

export function BulkStudentUploadDialog({ open, onOpenChange, onCommitted }) {
  const [fileName, setFileName] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [report, setReport] = useState(null);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);

  const reset = () => {
    setFileName("");
    setRawRows([]);
    setReport(null);
    setValidating(false);
    setCommitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);
    if (!parsed.length) {
      toast.error("CSV has no data rows — need a header row plus at least one student");
      return;
    }
    setFileName(file.name);
    setRawRows(parsed);
    setReport(null);
    setValidating(true);
    const res = await validateStudentApplicationsBulk(parsed);
    setValidating(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Validation failed");
      setReport(res.data?.detail && typeof res.data.detail === "object" ? res.data.detail : null);
      return;
    }
    setReport(res.data);
  };

  const errorRows = useMemo(
    () => (report?.rows || []).filter((r) => !r.ok),
    [report]
  );
  const okRows = useMemo(
    () => (report?.rows || []).filter((r) => r.ok),
    [report]
  );

  const commit = async () => {
    if (!report?.can_commit || !rawRows.length) return;
    setCommitting(true);
    const res = await commitStudentApplicationsBulk(rawRows);
    setCommitting(false);
    if (!res.ok) {
      const detail = res.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || "Commit blocked — fix errors and re-validate";
      toast.error(msg);
      if (detail && typeof detail === "object" && detail.summary) {
        setReport({ ...report, ...detail, can_commit: false, ok: false });
      }
      return;
    }
    toast.success(
      `Uploaded ${res.data.created || 0} new, reactivated ${res.data.reactivated || 0}`
    );
    handleClose(false);
    onCommitted?.();
  };

  const summary = report?.summary;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-student-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload application students</DialogTitle>
          <DialogDescription>
            Super Admin only. Validate the CSV first — nothing is written until you confirm the summary.
            Students still start registration via OTP on the Landing page after they appear on the roster.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-xl border border-[#E5E7EB] bg-[#F7F7F7] p-3 text-xs text-[#6B7280]">
            <div className="font-semibold text-[#111] mb-1">Expected columns</div>
            <code className="break-all">{SAMPLE_HEADERS.join(", ")}</code>
            <div className="mt-2">
              Required: <strong>application_number</strong> (use employee ID for course staff). Recommended: name, phone_number, mail_id, roll_no, student_category_id. Set <strong>skip_policies</strong> to <strong>true</strong> for employees who skip digital sign.
              If <strong>roll_no</strong> already belongs to a non-withdrawn application, that row is blocked.
            </div>
          </div>

          <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-[#D1D5DB] rounded-xl px-4 py-8 cursor-pointer hover:bg-[#FAFAFA]">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="bulk-student-csv-input"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            {validating ? (
              <Loader2 className="h-6 w-6 animate-spin text-[#B60F2A]" />
            ) : (
              <FileSpreadsheet className="h-6 w-6 text-[#B60F2A]" />
            )}
            <div className="font-medium text-[#111]">
              {fileName || "Choose CSV file"}
            </div>
            <div className="text-xs text-[#9CA3AF]">Click to select — validation runs automatically</div>
          </label>

          {report && summary && (
            <div className="space-y-3" data-testid="bulk-student-summary">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  ["Total", summary.total],
                  ["Create", summary.create],
                  ["Reactivate", summary.reactivate],
                  ["Errors", summary.error],
                  ["Warnings", summary.warning],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
                    <div className="text-lg font-semibold tabular-nums text-[#111]">{value}</div>
                  </div>
                ))}
              </div>

              {(report.notes || []).length > 0 && (
                <ul className="text-xs text-[#6B7280] list-disc pl-5 space-y-1">
                  {report.notes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}

              <div className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="px-3 py-2 bg-[#F7F7F7] text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
                  Rows that will be written ({okRows.length})
                </div>
                <div className="max-h-48 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[#9CA3AF] sticky top-0 bg-white">
                      <tr>
                        <th className="text-left px-3 py-1.5">#</th>
                        <th className="text-left px-3 py-1.5">App No</th>
                        <th className="text-left px-3 py-1.5">Name</th>
                        <th className="text-left px-3 py-1.5">Roll</th>
                        <th className="text-left px-3 py-1.5">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {okRows.slice(0, 100).map((r) => (
                        <tr key={`${r.row}-${r.application_number}`}>
                          <td className="px-3 py-1.5 tabular-nums">{r.row}</td>
                          <td className="px-3 py-1.5 font-medium">{r.application_number}</td>
                          <td className="px-3 py-1.5">{r.name || "—"}</td>
                          <td className="px-3 py-1.5">{r.roll_no || "—"}</td>
                          <td className="px-3 py-1.5">
                            <span className={r.action === "reactivate" ? "text-amber-700" : "text-emerald-700"}>
                              {r.action}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {okRows.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-[#9CA3AF]">No valid rows</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {okRows.length > 100 && (
                  <div className="px-3 py-2 text-[11px] text-[#9CA3AF]">Showing first 100 of {okRows.length}</div>
                )}
              </div>

              {errorRows.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 overflow-hidden">
                  <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-red-800 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Blocked rows ({errorRows.length})
                  </div>
                  <div className="max-h-40 overflow-auto bg-white">
                    {errorRows.slice(0, 50).map((r) => (
                      <div key={`err-${r.row}`} className="px-3 py-2 border-b border-red-100 text-xs">
                        <div className="font-medium text-[#111]">
                          Row {r.row}: {r.application_number || "(missing app no)"}
                          {r.roll_no ? ` · roll ${r.roll_no}` : ""}
                        </div>
                        <ul className="mt-1 text-red-700 list-disc pl-4">
                          {(r.errors || []).map((e) => <li key={e}>{e}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {okRows.some((r) => (r.warnings || []).length) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <div className="font-semibold mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Warnings (still allowed)
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5 max-h-28 overflow-auto">
                    {okRows.flatMap((r) =>
                      (r.warnings || []).map((w) => (
                        <li key={`${r.row}-${w}`}>Row {r.row} ({r.application_number}): {w}</li>
                      ))
                    ).slice(0, 40)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <button type="button" className="mu-btn-secondary text-sm" onClick={() => handleClose(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="mu-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
            disabled={!report?.can_commit || committing || validating}
            onClick={commit}
            data-testid="bulk-student-commit"
          >
            {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : report?.can_commit ? <CheckCircle2 className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
            {report?.can_commit
              ? `Confirm upload (${(summary?.create || 0) + (summary?.reactivate || 0)})`
              : "Confirm upload"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
